import React from "react";
import styled, { css } from "styled-components";
import {
  space,
  layout,
  typography,
  SpaceProps,
  LayoutProps,
  TypographyProps,
} from "styled-system";
import { FIELD_HEIGHT, fieldFrame, fieldInvalid } from "./fieldStyles";

type SelectElemProps = SpaceProps & LayoutProps & TypographyProps;

type SelectProps = SelectElemProps & {
  options: {
    value: string | null;
    label: string;
    disabled?: boolean;
  }[];
  /** Show the error state (same as aria-invalid). */
  invalid?: boolean | null;
} & React.ComponentPropsWithoutRef<"select">;

/** A thin chevron, drawn as a mask so it takes the theme's text colour. */
const CHEVRON = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='black' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;

const SelectWrapper = styled.div<
  SpaceProps & LayoutProps & TypographyProps & { invalid?: boolean }
>`
  width: 100%;
  cursor: pointer;
  line-height: 20px;
  display: grid;
  grid-template-areas: "select";
  align-items: center;
  ${fieldFrame(":focus-within")};
  ${space};
  ${layout};
  ${typography};

  &:after {
    content: "";
    grid-area: select;
    justify-self: end;
    width: 10px;
    height: 6px;
    margin-right: 12px;
    pointer-events: none;
    background-color: ${(p) => p.theme.colors.grey1};
    -webkit-mask: ${CHEVRON} center / contain no-repeat;
    mask: ${CHEVRON} center / contain no-repeat;
  }

  &:focus-within:after {
    background-color: ${(p) => p.theme.colors.primary};
  }

  &:has(select:disabled) {
    background-color: ${(p) => p.theme.colors.sunken};
    border-color: ${(p) => p.theme.colors.border};
    color: ${(p) => p.theme.colors.textMuted};
    cursor: not-allowed;
  }

  ${(p) =>
    p.invalid &&
    css`
      &,
      &:hover,
      &:focus-within {
        ${fieldInvalid};
      }
    `}
`;

const SelectElem = styled.select<SelectElemProps>`
  appearance: none;
  background-color: transparent;
  min-height: ${FIELD_HEIGHT - 2}px;
  padding: 6px 34px 6px 12px;
  margin: 0;
  font-family: inherit;
  border: 0;
  border-radius: inherit;
  color: inherit;
  font-size: inherit;
  cursor: inherit;
  line-height: inherit;
  display: block;
  width: 100%;
  grid-area: select;
  outline: 0;
`;

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ options, size, value, onChange, style, invalid, ...rest }, ref) => (
    <SelectWrapper style={style} invalid={invalid ?? undefined} {...(rest as any)}>
      <SelectElem
        {...rest}
        aria-invalid={invalid ? true : rest["aria-invalid"]}
        ref={ref}
        value={value}
        onChange={onChange}
      >
        {options.map(({ value, label, disabled }) => (
          <option
            key={value}
            value={value?.toString() || ""}
            disabled={disabled}
          >
            {label}
          </option>
        ))}
      </SelectElem>
    </SelectWrapper>
  ),
);

export default Select;
